import React, { useEffect, useState, useCallback } from "react";
import {
  RemoteAudioTrack,
  RemoteVideoTrack,
  RemoteTrack,
  RemoteParticipant,
  RemoteAudioTrackPublication,
  RemoteVideoTrackPublication,
} from "twilio-video";
import ErrorBoundary from "../ErrorBoundary";
import {
  forgetRemoteAudio,
  playRemoteAudio,
} from "src/util/audio-playback.util";
import {
  StyledAudio,
  StyledRemoteParticipantVideo,
} from "../remoteParticipantVideo/RemoteParticipantVideoStyles";

function notNull<T>(x: T | null): x is T {
  return x !== null;
}

const getTracksFromPublications = (
  tracks: Array<RemoteAudioTrackPublication | RemoteVideoTrackPublication>
): Array<RemoteAudioTrack | RemoteVideoTrack> => {
  return tracks
    .map(
      (track: RemoteAudioTrackPublication | RemoteVideoTrackPublication) =>
        track.track
    )
    .filter(notNull);
};

// Custom hook to manage audio and video tracks
const useTrackManagement = (participant: RemoteParticipant) => {
  const [audioTracks, setAudioTracks] = useState<RemoteAudioTrack[]>([]);

  const handleTrackSubscribed = useCallback((track: RemoteTrack) => {
    console.log("handleTrackSubscribed");
    if (track.kind === "audio") {
      setAudioTracks((tracks) => [...tracks, track]);
    }
  }, []);

  const handleTrackUnsubscribed = useCallback((track: RemoteTrack) => {
    console.log("handleTrackUnsubscribed");
    if (track.kind === "audio") {
      setAudioTracks((tracks) => [
        ...tracks.filter((_track) => _track !== track),
      ]);
    }
  }, []);

  useEffect(() => {
    const participantAudioTracks = getTracksFromPublications([
      ...participant.audioTracks.values(),
    ]);
    setAudioTracks(participantAudioTracks as RemoteAudioTrack[]);

    participant.on("trackSubscribed", handleTrackSubscribed);

    participant.on("trackUnsubscribed", handleTrackUnsubscribed);

    return () => {
      participant.off("trackSubscribed", handleTrackSubscribed);
      participant.off("trackUnsubscribed", handleTrackUnsubscribed);
    };
  }, [handleTrackSubscribed, handleTrackUnsubscribed, participant]);

  return { audioTracks };
};

export interface RemoteParticipantAudioProps {
  participant: RemoteParticipant;
  muteSpeaker?: boolean;
}

const RemoteParticipantAudioOnly: React.FC<RemoteParticipantAudioProps> = ({
  participant,
  muteSpeaker,
}) => {
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(
    null
  );

  const { audioTracks } = useTrackManagement(participant);

  useEffect(() => {
    const audioTrack = audioTracks[0];

    if (audioTrack && audioElement) {
      audioTrack.attach(audioElement);
      playRemoteAudio(audioElement);
    }

    return () => {
      audioTrack?.detach();
      if (audioElement) {
        forgetRemoteAudio(audioElement);
      }
    };
  }, [audioTracks, audioElement]);

  useEffect(() => {
    if (audioElement) {
      if (muteSpeaker) {
        audioElement!.muted = true;
      } else {
        audioElement!.muted = false;
      }
    }
  }, [muteSpeaker, audioElement]);

  return (
    <ErrorBoundary>
      <StyledRemoteParticipantVideo>
        <StyledAudio
          ref={(audio) => setAudioElement(audio)}
          autoPlay
          muted={false}
        />
      </StyledRemoteParticipantVideo>
    </ErrorBoundary>
  );
};

export default RemoteParticipantAudioOnly;
